import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Table,
  TableProps,
  Box,
  SpaceBetween,
  Button,
  Header,
  Alert,
  Link,
  Pagination,
} from '@amzn/awsui-components-console';
import { ApolloError } from '@apollo/client';
import { useNavigate } from 'react-router';
import { SearchState, SEARCH_MODE } from '../SearchPage';
import {
  useSearchSourceStringsLazyQuery,
  useSearchTranslationsLazyQuery,
  SourceStringResult,
  TranslationResult,
} from 'src/generated/graphql';
import { TranslationStatusIndicator } from 'src/common/translationStatus';
import {
  SORT_FIELD_LABELS,
  toggleSortField,
  toServerSort,
  type SortField,
  type SortState,
} from '../sortState';
import SortableColumnHeader from './SortableColumnHeader';
import { MIN_SEARCH_CRITERIA_MESSAGE } from '../constants';
import { hasMinimumSearchCriteria } from '../searchFilterUtils';
import styles from './SearchResultsTable.module.scss';

interface SearchResultsTableProps {
  readonly searchState: SearchState;
  readonly triggerSearch: number;
  readonly onSortChange: (sortState: SortState) => void;
}

/**
 * What the most recent search request actually put on the wire, remembered so
 * the ordering label can describe the rows ON SCREEN rather than the live form
 * state.
 *
 * The rendered rows come from the LAST SUBMITTED request, but `searchState` is
 * the live form: typing in the query box updates `searchState.queryText`
 * without issuing a request (`handleQueryChange`, not `doSearch`). Deriving the
 * label from the prop therefore let it flip to "Sorted by relevance" while the
 * rows were still the recency-ordered browse results. Only the two fields the
 * label depends on are captured — this is not a copy of the whole request.
 */
interface RequestOrderingSnapshot {
  /**
   * `queryText` exactly as transmitted, i.e. already trimmed. An empty string
   * means the request carried NO query text (browse mode), which is the input
   * to the backend's recency branch.
   */
  readonly queryText: string;
  /**
   * Whether the request carried an explicit `sortBy`. When it did, the sorted
   * column's own header indicator names the ordering and the label stays quiet.
   */
  readonly hasExplicitSort: boolean;
}

// --- Hyperlink helpers ---
// TODO: Replace '#' with real route templates once detail pages exist in App.tsx.
// These are structured as one-line-change helpers so swapping in real routes later
// only requires updating the return value here.

/** Build href for a string detail page. */
function buildStringDetailHref(_row: {
  namespaceId: string;
  stringGroupId: string;
  stringId: string;
}): string {
  // TODO: return `/${row.namespaceId}/${row.stringGroupId}/${row.stringId}`;
  return '#';
}

/** Build href for a namespace detail page. */
function buildNamespaceHref(row: { namespaceId: string }): string {
  return `/${encodeURIComponent(row.namespaceId)}`;
}

/** Build href for a string group detail page. */
function buildStringGroupHref(_row: {
  namespaceId: string;
  stringGroupId: string;
}): string {
  // TODO: return `/${row.namespaceId}/${row.stringGroupId}`;
  return '#';
}

/**
 * Internal SPA link for namespace navigation.
 * Uses Cloudscape Link with onFollow + react-router navigate to avoid full page reload,
 * while preserving href for middle-click / Cmd+click open-in-new-tab.
 * Matches the navigation pattern used in NamespaceSideNav.
 */
const NamespaceLink: React.FC<{ namespaceId: string }> = ({ namespaceId }) => {
  const navigate = useNavigate();
  const href = buildNamespaceHref({ namespaceId });

  return (
    <Link
      href={href}
      onFollow={(event) => {
        event.preventDefault();
        navigate(href);
      }}
    >
      {namespaceId}
    </Link>
  );
};

/**
 * Row action for opening a string's detail page.
 *
 * A Cloudscape `Button` rather than a `Link` (it is an action, not inline text),
 * navigating to the SAME target the hyperlink used. `ariaLabel` names the row so
 * a screen reader user hears which string a "Details" button belongs to.
 */
const DetailsButton: React.FC<{
  readonly item: {
    namespaceId: string;
    stringGroupId: string;
    stringId: string;
  };
}> = ({ item }) => {
  const navigate = useNavigate();
  const href = buildStringDetailHref(item);

  return (
    <Button
      variant="normal"
      ariaLabel={`View details for ${item.stringId}`}
      onClick={() => navigate(href)}
    >
      Details
    </Button>
  );
};

/**
 * Extracts user-friendly error info from an Apollo error result.
 * Backend returns InternalServerException (500) for downstream/search failures
 * and ValidationException (400) for input validation errors.
 */
function classifyError(error: ApolloError): {
  type: 'server' | 'validation';
  message: string;
} {
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    const validationError = error.graphQLErrors.find(
      (e) =>
        (e.extensions?.['code'] as string | undefined) === 'VALIDATION_ERROR' ||
        (e.extensions?.['errorType'] as string | undefined) ===
          'ValidationException',
    );
    if (validationError) {
      return { type: 'validation', message: validationError.message };
    }
  }
  return { type: 'server', message: '' };
}

/** Page size used for all search requests */
const PAGE_SIZE = 25;

/**
 * Pagination design: Sequential-prefetch with page-token cache.
 *
 * The backend uses cursor-based pagination (search_after). There is no way to
 * jump directly to page N without having fetched pages 1..N-1 to obtain their
 * nextTokens. To support Cloudscape's click-to-jump <Pagination> component, we
 * maintain a client-side cache mapping page numbers to their cursor tokens:
 *
 *   pageTokenCache: Map<number, string | undefined>
 *     - Key: 1-based page number
 *     - Value: the token needed to fetch that page (undefined for page 1)
 *
 * Behavior:
 *   - Clicking a previously visited page (token cached): single fetch using
 *     the cached token — O(1) network cost.
 *   - Clicking a page beyond the furthest visited: sequentially fetches each
 *     intermediate page to collect their nextTokens, then fetches the target
 *     page — O(N) network cost for N pages ahead. Each fetch depends on the
 *     previous page's returned nextToken, so they CANNOT be parallelized.
 *   - Cache is CLEARED on every new search/sort/filter change to avoid stale
 *     cursors from a different result set.
 *
 * Cost tradeoff (deliberate, reviewed):
 *   - First visit to page N: O(N) sequential fetches
 *   - Revisiting any cached page: O(1) single fetch
 *   - This is acceptable because users typically paginate sequentially and the
 *     total result set has a practical upper bound. The alternative (no jump)
 *     forces N sequential clicks anyway.
 *
 * Bound: The visible page count shown to the user is capped at MAX_VISIBLE_PAGES
 * (currently 20). This limits the worst-case sequential prefetch to at most
 * MAX_VISIBLE_PAGES - 1 fetches when jumping from page 1 to the last visible page.
 */

/**
 * Maximum number of pages exposed to the user via the Pagination component.
 * Bounds the worst-case sequential prefetch to MAX_VISIBLE_PAGES - 1 fetches.
 */
const MAX_VISIBLE_PAGES = 20;

/** Truncates long cell text, keeping the full value in a tooltip. */
const TruncatedText: React.FC<{ readonly text: string }> = ({ text }) => (
  <span title={text}>{text.length > 80 ? `${text.slice(0, 80)}…` : text}</span>
);

/**
 * Builds the source-mode columns. Sortable headers are `SortableColumnHeader`
 * buttons; default widths are set because `resizableColumns` requires numeric
 * widths to seed the user-draggable layout.
 */
function createSourceColumnDefinitions(
  sortState: SortState,
  onToggleSort: (field: SortField) => void,
): TableProps.ColumnDefinition<SourceStringResult>[] {
  const sortable = (field: SortField) => (
    <SortableColumnHeader
      label={SORT_FIELD_LABELS[field]}
      field={field}
      sortState={sortState}
      onToggleSort={onToggleSort}
    />
  );

  return [
    {
      id: 'stringId',
      header: sortable('STRING_ID'),
      width: 260,
      minWidth: 140,
      cell: (item) => (
        <span data-testid="row-string-id">
          <Link href={buildStringDetailHref(item)}>{item.stringId}</Link>
        </span>
      ),
    },
    {
      id: 'namespace',
      header: sortable('NAMESPACE'),
      width: 180,
      minWidth: 120,
      cell: (item) => <NamespaceLink namespaceId={item.namespaceId} />,
    },
    {
      id: 'group',
      header: sortable('STRING_GROUP'),
      width: 170,
      minWidth: 120,
      cell: (item) => (
        <Link href={buildStringGroupHref(item)}>{item.stringGroupId}</Link>
      ),
    },
    {
      id: 'status',
      header: sortable('STATUS'),
      width: 150,
      minWidth: 120,
      cell: (item) => <TranslationStatusIndicator status={item.status} />,
    },
    {
      id: 'sourceContent',
      header: 'Source text',
      width: 300,
      minWidth: 160,
      cell: (item) => <TruncatedText text={item.sourceContent} />,
    },
    {
      id: 'actions',
      header: '',
      width: 120,
      minWidth: 100,
      cell: (item) => <DetailsButton item={item} />,
    },
  ];
}

/**
 * Translation-mode columns.
 *
 * `STATUS` is source-only — `TranslationResult` has no `status` member, so the
 * column cannot be rendered here even though the mockup shows one.
 *
 * Locale IS rendered here (translation mode only): `TranslationResult` carries a
 * required `locale`, whereas `SourceStringResult` has no equivalent member, so
 * the column must not be added to the source definitions.
 */
function createTranslationColumnDefinitions(
  sortState: SortState,
  onToggleSort: (field: SortField) => void,
): TableProps.ColumnDefinition<TranslationResult>[] {
  const sortable = (field: SortField) => (
    <SortableColumnHeader
      label={SORT_FIELD_LABELS[field]}
      field={field}
      sortState={sortState}
      onToggleSort={onToggleSort}
    />
  );

  return [
    {
      id: 'stringId',
      header: sortable('STRING_ID'),
      width: 260,
      minWidth: 140,
      cell: (item) => (
        <span data-testid="row-string-id">
          <Link href={buildStringDetailHref(item)}>{item.stringId}</Link>
        </span>
      ),
    },
    {
      id: 'namespace',
      header: sortable('NAMESPACE'),
      width: 180,
      minWidth: 120,
      cell: (item) => <NamespaceLink namespaceId={item.namespaceId} />,
    },
    {
      id: 'group',
      header: sortable('STRING_GROUP'),
      width: 170,
      minWidth: 120,
      cell: (item) => (
        <Link href={buildStringGroupHref(item)}>{item.stringGroupId}</Link>
      ),
    },
    {
      id: 'localizedText',
      header: 'Localized text',
      width: 320,
      minWidth: 160,
      cell: (item) => <TruncatedText text={item.localizedText} />,
    },
    /*
     * Locale is deliberately a PLAIN column, not a `SortableColumnHeader`.
     *
     * The backend `SortBy` enum has no LOCALE value, and all ordering is
     * server-side, so a clickable header here could not sort anything at all. It
     * would be a dead control that looks like a global sort. Make it sortable
     * only once `SortBy` gains a LOCALE value (add 'LOCALE' to `SortField` +
     * `SERVER_SORT_BY` in `sortState.ts` at the same time).
     */
    {
      id: 'locale',
      header: 'Locale',
      width: 140,
      minWidth: 100,
      cell: (item) => <span data-testid="row-locale">{item.locale}</span>,
    },
    {
      id: 'actions',
      header: '',
      width: 120,
      minWidth: 100,
      cell: (item) => <DetailsButton item={item} />,
    },
  ];
}

const SearchResultsTable: React.FC<SearchResultsTableProps> = ({
  searchState,
  triggerSearch,
  onSortChange,
}) => {
  const isSource = searchState.mode === SEARCH_MODE.SOURCE;
  const { sortState } = searchState;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [validationWarning, setValidationWarning] = useState<string | null>(
    null,
  );
  // Whether we're in a multi-page prefetch sequence (jumping to an unvisited page)
  const [isPrefetching, setIsPrefetching] = useState(false);

  /*
   * Snapshot of the ordering-relevant parts of the LAST ISSUED request — the
   * source of truth for the ordering label. `null` until the first request.
   *
   * State, not a ref, because the label has to re-render when it changes; it is
   * safe to set here because NO effect depends on it (the two search effects key
   * off `triggerSearch` / `sortState` and invoke `doSearchRef.current`), so
   * setting it cannot retrigger a search.
   */
  const [orderingSnapshot, setOrderingSnapshot] =
    useState<RequestOrderingSnapshot | null>(null);

  // Page-token cache: maps page number to the token needed to fetch that page.
  // Page 1 always maps to undefined (no token needed for the first page).
  // This ref avoids re-renders on cache updates; state changes from fetches
  // drive the necessary re-renders.
  const pageTokenCache = useRef<Map<number, string | undefined>>(
    new Map([[1, undefined]]),
  );
  // Track the highest page we have a nextToken for (i.e., the furthest page
  // whose response we've seen, meaning we know the token for page N+1)
  const furthestPageFetched = useRef<number>(0);

  // Generation counter to cancel in-flight sequential prefetches when a new
  // search/sort/mode change resets pagination or a newer page-jump supersedes
  // an older one. Each increment invalidates all prior async loops.
  const searchGeneration = useRef<number>(0);

  const previousTrigger = useRef(0);
  const hasSearchedAtLeastOnce = useRef(false);

  const [executeSourceSearch, sourceResult] = useSearchSourceStringsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [executeTranslationSearch, translationResult] =
    useSearchTranslationsLazyQuery({
      fetchPolicy: 'network-only',
    });

  const result = isSource ? sourceResult : translationResult;
  const loading = result.loading || isPrefetching;
  const error = result.error;

  /*
   * The single criteria gate for the whole component. Returns the warning to
   * surface, or null when the current `searchState` may be sent to the backend.
   *
   * `SearchPage`/`SearchBar` already gate every user-initiated SUBMIT path, so
   * this exists for the paths that re-fire an EXISTING search after the user
   * has emptied the query: the sort-change effect, Retry, and pagination.
   *
   * It is deliberately a standalone function rather than living inside
   * `doSearch`, because there are TWO request-issuing paths and only one of
   * them goes through `doSearch`:
   *   1. `doSearch` — new search, sort change, Retry, and cached-token page fetch
   *   2. `handlePageChange`'s sequential prefetch loop — builds its own request
   *      variables and calls the lazy-query executors DIRECTLY
   * Path 2 inherits nothing from `doSearch`, so both callers must consult this
   * gate. Every call site of `executeSourceSearch`/`executeTranslationSearch`
   * sits behind one of those two, and both are guarded.
   *
   * This is a UI restriction, not a backend one: the API deliberately treats an
   * empty `queryText` as browse mode (match_all). See `hasMinimumSearchCriteria`.
   */
  const criteriaWarning = useCallback((): string | null => {
    const { queryText, filters } = searchState;

    // TRANSLATION mode — translatedLocales is required
    if (
      !isSource &&
      (!filters.translatedLocales || filters.translatedLocales.length === 0)
    ) {
      return 'At least one translated locale is required for translation search.';
    }
    // SOURCE mode — at least one criterion is required
    if (isSource && !hasMinimumSearchCriteria(filters, queryText)) {
      return MIN_SEARCH_CRITERIA_MESSAGE;
    }
    return null;
  }, [searchState, isSource]);

  const doSearch = useCallback(
    (token?: string) => {
      const { queryText, exactMatch, filters } = searchState;

      const warning = criteriaWarning();
      if (warning) {
        setValidationWarning(warning);
        return;
      }
      setValidationWarning(null);

      /*
       * The sort state maps onto AT MOST one backend sort pair. SPREAD, not
       * two named fields: an unsorted state must omit both KEYS so the backend
       * takes its own resolution path (relevance for a keyword search, recency
       * for a browse) rather than receiving `sortBy: undefined`.
       */
      const serverSort = toServerSort(sortState);

      // `.trim()` so the request agrees with `hasMinimumSearchCriteria`,
      // which also trims: whitespace-only text is not a criterion, so it must
      // not be sent as one either (plain `queryText || undefined` would let
      // "   " through, since only '' coerces to undefined).
      const trimmedQueryText = queryText.trim();

      /*
       * Record what this request transmits, at the moment it is issued — AFTER
       * the criteria gate above, so a skipped request leaves the previous
       * snapshot (and therefore the label) describing the rows still on screen.
       */
      setOrderingSnapshot({
        queryText: trimmedQueryText,
        hasExplicitSort: serverSort.sortBy !== undefined,
      });

      const baseInput = {
        queryText: trimmedQueryText || undefined,
        exactMatch,
        filters: {
          namespaceIds: filters.namespaceIds,
          stringGroupIds: filters.stringGroupIds,
          cxIds: filters.cxIds,
          cxFamilies: filters.cxFamilies,
          translatedLocales: filters.translatedLocales,
        },
        ...serverSort,
        pageSize: PAGE_SIZE,
        nextToken: token,
      };

      if (isSource) {
        executeSourceSearch({
          variables: {
            input: {
              ...baseInput,
              filters: {
                ...baseInput.filters,
                statuses: filters.statuses,
              },
            },
          },
        });
      } else {
        executeTranslationSearch({
          variables: { input: baseInput },
        });
      }
    },
    [
      searchState,
      sortState,
      isSource,
      criteriaWarning,
      executeSourceSearch,
      executeTranslationSearch,
    ],
  );

  const doSearchRef = useRef(doSearch);
  doSearchRef.current = doSearch;

  /** Reset pagination state — called on new search, sort, or filter change */
  const resetPagination = useCallback(() => {
    searchGeneration.current++;
    setCurrentPage(1);
    pageTokenCache.current = new Map([[1, undefined]]);
    furthestPageFetched.current = 0;
    setIsPrefetching(false);
  }, []);

  // Reset pagination state when search mode changes to prevent stale
  // page-token cache and totalCount from the other mode leaking through.
  const previousMode = useRef(searchState.mode);
  useEffect(() => {
    if (searchState.mode !== previousMode.current) {
      previousMode.current = searchState.mode;
      resetPagination();
      setTotalCount(0);
      setValidationWarning(null);
      /*
       * Switching mode swaps which lazy query's data is rendered WITHOUT issuing
       * a request, so the remembered request no longer describes the rows on
       * screen. Clear it (the label disappears) until the next request records a
       * new one — same reason the counts are cleared here.
       */
      setOrderingSnapshot(null);
    }
  }, [searchState.mode, resetPagination]);

  // React to search trigger (new search from button click)
  useEffect(() => {
    if (triggerSearch > 0 && triggerSearch !== previousTrigger.current) {
      previousTrigger.current = triggerSearch;
      hasSearchedAtLeastOnce.current = true;
      resetPagination();
      doSearchRef.current(undefined);
    }
  }, [triggerSearch, resetPagination]);

  /*
   * Re-query on EVERY sort change. Ordering is entirely server-side, so a sort
   * change can only take effect through a new request — and it must start from
   * page 1: the cached `search_after` page tokens are cursors into the ORDER
   * that produced them, so they are meaningless under a different sort.
   * `resetPagination` is what clears that cache (along with `currentPage` and
   * the in-flight prefetch generation), so it must keep running here.
   *
   * The optional chaining is what covers the UNSORTED state: leaving it
   * (`undefined` → a field) and entering it (a field → `undefined`) both change
   * these dependencies, so the third click of the cycle refetches and resets
   * pagination exactly like any other sort change. That matters because an
   * unsorted request is ordered by relevance or recency — a cursor from an
   * explicit column sort is no more valid under it than under a different column.
   */
  useEffect(() => {
    if (hasSearchedAtLeastOnce.current) {
      resetPagination();
      doSearchRef.current(undefined);
    }
  }, [sortState?.field, sortState?.order, resetPagination]);

  // Update totalCount and page-token cache from results
  useEffect(() => {
    // During sequential prefetch the loop manages the cache itself;
    // skip to avoid corrupting entries with stale currentPage.
    if (isPrefetching) return;

    const conn = isSource
      ? sourceResult.data?.searchSourceStrings
      : translationResult.data?.searchTranslations;

    if (conn) {
      setTotalCount(conn.totalCount);
      // Cache the nextToken for the NEXT page (currentPage + 1)
      if (conn.nextToken) {
        pageTokenCache.current.set(currentPage + 1, conn.nextToken);
      }
      // Track furthest page we've fetched
      if (currentPage > furthestPageFetched.current) {
        furthestPageFetched.current = currentPage;
      }
    }
  }, [
    isPrefetching,
    isSource,
    sourceResult.data,
    translationResult.data,
    currentPage,
  ]);

  /**
   * Handle page change from Cloudscape Pagination component.
   * If the target page's token is cached, fetch directly.
   * If not, sequentially fetch intermediate pages to build up the cache.
   */
  const handlePageChange = useCallback(
    async (targetPage: number) => {
      // Same page — no-op
      if (targetPage === currentPage) return;

      /*
       * Criteria gate — MUST run here, before either request path below.
       *
       * Editing the query text does not reset pagination, so Pagination stays
       * enabled after the user empties the query. The cached-token path would
       * be caught by `doSearch`, but the prefetch loop further down builds its
       * own request variables and calls the executors directly, so it would
       * otherwise fire up to MAX_VISIBLE_PAGES - 1 unbounded browse-mode
       * queries at once. Gating the whole handler covers both paths.
       */
      const warning = criteriaWarning();
      if (warning) {
        setValidationWarning(warning);
        return;
      }
      setValidationWarning(null);

      const cachedToken = pageTokenCache.current.get(targetPage);
      if (cachedToken !== undefined || targetPage === 1) {
        // Token is cached (or it's page 1 which needs no token) — direct fetch
        setCurrentPage(targetPage);
        doSearchRef.current(targetPage === 1 ? undefined : cachedToken);
        return;
      }

      // Target page is beyond our cached range — need to sequentially fetch
      // intermediate pages to collect their nextTokens.
      const gen = ++searchGeneration.current;
      setIsPrefetching(true);

      // Find the highest cached page from which we can start
      let startPage = 1;
      for (let p = targetPage - 1; p >= 1; p--) {
        if (pageTokenCache.current.has(p)) {
          // We need the token for page p+1 to exist. If page p was fetched,
          // then p+1's token should be cached (from the response of page p).
          // But if only page p's ENTRY token is cached (not its response),
          // we need to start from the last page we actually fetched.
          if (p <= furthestPageFetched.current) {
            startPage = p + 1;
            break;
          }
        }
      }

      // If startPage's token isn't cached, fall back to page after furthest
      /* istanbul ignore next -- defensive invariant: pageTokenCache always contains page 1 and the loop above only sets startPage to p+1 for a fetched page whose successor token is cached, so this branch is unreachable via valid pagination flow */
      if (!pageTokenCache.current.has(startPage)) {
        startPage = furthestPageFetched.current + 1;
      }

      // Sequential fetch loop: fetch pages startPage..targetPage
      const { queryText, exactMatch, filters } = searchState;
      // Spread, like `doSearch` — an unsorted state must omit both sort keys.
      const serverSort = toServerSort(sortState);
      // Trimmed for the same reason as in `doSearch` — keep the two request
      // builders byte-identical in what they transmit.
      const trimmedQueryText = queryText.trim();

      /*
       * The SECOND request-issuing path, so it must record the snapshot too —
       * `doSearch` is not involved here, this loop calls the executors directly.
       * Every page in the loop transmits the same `queryText`/sort, so one set
       * covers all of them.
       *
       * It cannot leave a stale value behind: this runs synchronously with the
       * `gen` increment above (nothing between them awaits), so it is always the
       * newest snapshot at the moment it is written, and the loop never writes
       * again. A newer request that supersedes this generation therefore always
       * writes AFTER this, and its value wins for good — a superseded loop
       * cannot overwrite it on a later iteration, because it returns at the
       * first `gen !== searchGeneration.current` check instead of continuing.
       */
      setOrderingSnapshot({
        queryText: trimmedQueryText,
        hasExplicitSort: serverSort.sortBy !== undefined,
      });

      for (let page = startPage; page <= targetPage; page++) {
        const token = pageTokenCache.current.get(page);
        /* istanbul ignore next -- unreachable: page 1's token is legitimately undefined; every other page's token is a non-null string cached from the prior page's server response, so a non-page-1 undefined token is an impossible cache state */
        if (token === undefined && page !== 1) {
          setIsPrefetching(false);
          return;
        }

        const baseInput = {
          queryText: trimmedQueryText || undefined,
          exactMatch,
          filters: {
            namespaceIds: filters.namespaceIds,
            stringGroupIds: filters.stringGroupIds,
            cxIds: filters.cxIds,
            cxFamilies: filters.cxFamilies,
            translatedLocales: filters.translatedLocales,
          },
          ...serverSort,
          pageSize: PAGE_SIZE,
          nextToken: token,
        };

        const variables = isSource
          ? {
              input: {
                ...baseInput,
                filters: { ...baseInput.filters, statuses: filters.statuses },
              },
            }
          : { input: baseInput };

        try {
          let data:
            | { nextToken?: string | null; totalCount: number }
            | null
            | undefined;

          if (isSource) {
            const response = await executeSourceSearch({ variables });
            // useLazyQuery's execute() always resolves (even on GraphQL
            // errors) — it never rejects. Explicitly throw on a populated
            // error field so this is caught by the catch block below,
            // consistent with Apollo's documented execute() behavior:
            // "returns a promise that fulfills with a query result when
            // the query succeeds or fails."
            if (response.error) throw response.error;
            // Bail out if a reset or newer invocation superseded this one
            if (gen !== searchGeneration.current) {
              setIsPrefetching(false);
              return;
            }
            data = response.data?.searchSourceStrings;
          } else {
            const response = await executeTranslationSearch({ variables });
            if (response.error) throw response.error;
            // Bail out if a reset or newer invocation superseded this one
            if (gen !== searchGeneration.current) {
              setIsPrefetching(false);
              return;
            }
            data = response.data?.searchTranslations;
          }

          if (data?.nextToken) {
            pageTokenCache.current.set(page + 1, data.nextToken);
          }
          if (page > furthestPageFetched.current) {
            furthestPageFetched.current = page;
          }
          if (data) {
            setTotalCount(data.totalCount);
          }
        } catch {
          // If any intermediate fetch fails, stop and stay where we landed
          setIsPrefetching(false);
          setCurrentPage(page);
          return;
        }
      }

      setIsPrefetching(false);
      // Only update currentPage if this generation is still active
      if (gen === searchGeneration.current) {
        setCurrentPage(targetPage);
      }
    },
    [
      currentPage,
      searchState,
      sortState,
      isSource,
      criteriaWarning,
      executeSourceSearch,
      executeTranslationSearch,
    ],
  );

  /** Header activation → next sort state (triggers a refetch from page 1). */
  const handleToggleSort = useCallback(
    (field: SortField) => {
      onSortChange(toggleSortField(sortState, field));
    },
    [onSortChange, sortState],
  );

  const sourceColumnDefinitions = useMemo(
    () => createSourceColumnDefinitions(sortState, handleToggleSort),
    [sortState, handleToggleSort],
  );
  const translationColumnDefinitions = useMemo(
    () => createTranslationColumnDefinitions(sortState, handleToggleSort),
    [sortState, handleToggleSort],
  );

  /*
   * Rows are rendered in EXACTLY the order the backend returned them. There is
   * no client-side re-sort: the sort state was sent with the request, so
   * reordering here could only disagree with the collation the backend
   * paginated by and make the sequence non-monotonic across pages. See
   * `sortState.ts` module docs.
   *
   * `useMemo` only keeps the empty-array identity stable so `Table` does not see
   * a new `items` array on every render.
   */
  const sourceData = sourceResult.data?.searchSourceStrings?.items;
  const translationData = translationResult.data?.searchTranslations?.items;

  const sourceItems = useMemo(() => sourceData ?? [], [sourceData]);
  const translationItems = useMemo(
    () => translationData ?? [],
    [translationData],
  );

  const hasSearched = triggerSearch > 0;

  // Compute total pages from totalCount
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1;
  // Cap visible pages to bound worst-case sequential prefetch to MAX_VISIBLE_PAGES - 1 fetches
  const visiblePages = Math.min(totalPages, MAX_VISIBLE_PAGES);

  /*
   * Two DISTINCT empty states:
   * - before any search has run: tell the user what to do
   * - a search returned zero rows: tell them how to widen it
   */
  const emptyContent = hasSearched ? (
    <Box
      textAlign="center"
      color="inherit"
      padding={{ top: 'l', bottom: 'l' }}
      data-testid="no-results-empty-state"
    >
      <SpaceBetween size="xxs">
        <b>No results found</b>
        <Box variant="p" color="text-body-secondary">
          Try adjusting your search or filters.
        </Box>
      </SpaceBetween>
    </Box>
  ) : (
    <Box
      textAlign="center"
      color="inherit"
      padding={{ top: 'l', bottom: 'l' }}
      data-testid="pre-search-empty-state"
    >
      <SpaceBetween size="xxs">
        <b>Search for strings</b>
        <Box variant="p" color="text-body-secondary">
          Enter a search query and click Search to find strings.
        </Box>
      </SpaceBetween>
    </Box>
  );

  // Criteria gate warning — unmet source-mode criteria, or translation mode
  // with no locale selected (see `criteriaWarning`)
  if (validationWarning && hasSearched) {
    return (
      <Alert type="warning" statusIconAriaLabel="Warning">
        {validationWarning}
      </Alert>
    );
  }

  // Error handling
  if (error && hasSearched) {
    const classified = classifyError(error);

    if (classified.type === 'validation') {
      return (
        <Alert
          type="warning"
          statusIconAriaLabel="Warning"
          header="Validation error"
        >
          {classified.message}
        </Alert>
      );
    }

    return (
      <Alert type="error" statusIconAriaLabel="Error" header="Search failed">
        An error occurred while searching. This may be caused by an upstream
        service dependency issue. Please try again in a few minutes.
        <Box margin={{ top: 's' }}>
          <Button
            onClick={() => doSearch(pageTokenCache.current.get(currentPage))}
          >
            Retry
          </Button>
        </Box>
      </Alert>
    );
  }

  const paginationComponent = hasSearched && totalCount > 0 && (
    <Pagination
      currentPageIndex={currentPage}
      pagesCount={visiblePages}
      disabled={loading}
      onChange={({ detail }) => handlePageChange(detail.currentPageIndex)}
      ariaLabels={{
        nextPageLabel: 'Next page',
        previousPageLabel: 'Previous page',
        pageLabel: (pageNumber) => `Page ${pageNumber}`,
      }}
    />
  );

  /*
   * Names the ordering the user is actually looking at, INFERRED from the request
   * this component sent — read off `orderingSnapshot`, the record of what the
   * last issued request transmitted, NOT off the live `searchState` prop:
   *
   *   no sortBy sent + query text sent → 'Sorted by relevance'
   *   no sortBy sent + no query text sent → 'Sorted by most recently updated'
   *   explicit sortBy sent → nothing; that header's own sort indicator already
   *                          says it, and repeating it here would be redundant.
   *
   * The unsorted state is the case that needs a label: no `sortBy` is sent, so no
   * header is marked, and the ordering the backend picked would otherwise be
   * invisible. Rendered as the Results `Header` description — Cloudscape's
   * idiomatic slot for secondary context about a table, and no new control.
   *
   * WHY THE SNAPSHOT rather than the prop: the rows on screen are from the last
   * SUBMITTED search, while `searchState` is the live form. Typing in the query
   * box goes through `handleQueryChange`, not `doSearch`, so it changes
   * `searchState.queryText` without refetching — deriving the label from the prop
   * made it flip to "Sorted by relevance" while the rows were still the
   * recency-ordered browse results. The snapshot is written by BOTH
   * request-issuing paths (`doSearch` and the prefetch loop in
   * `handlePageChange`) at the point the request goes out, and by neither when a
   * request is skipped, so it always describes the displayed rows. Its presence
   * doubles as "a request has been issued", which is why there is no `hasSearched`
   * term here.
   *
   * CAVEAT — this is inference, not a report. The response does not echo which
   * sort branch the backend applied, so the label is the UI restating its own
   * request against a remembered copy of the resolution order in
   * `OpenSearchQueryHelper.buildPrimarySort` (explicit `sortBy` → `_score` desc
   * when query text is present → `last_updated_at` desc; see `sortState.ts`). If
   * that resolution order ever changes, this label silently drifts and must be
   * changed with it. Having the backend report the applied sort in the search
   * response would remove the guesswork and let this read it instead of deriving
   * it.
   */
  const effectiveOrdering =
    orderingSnapshot && !orderingSnapshot.hasExplicitSort
      ? orderingSnapshot.queryText
        ? 'Sorted by relevance'
        : 'Sorted by most recently updated'
      : undefined;

  const resultsHeader = (
    <Header
      counter={hasSearched ? `(${totalCount})` : undefined}
      description={
        effectiveOrdering && (
          <span data-testid="effective-ordering">{effectiveOrdering}</span>
        )
      }
    >
      Results
    </Header>
  );

  /*
   * `variant="container"` makes the table its own panel (border + header),
   * separate from the Filters panel above it.
   * The wrapper div carries the link styling (no underline in any state, plus
   * the hover/focus affordance that replaces it) — see the SCSS.
   */
  return (
    <div className={styles.tableWrapper}>
      {isSource ? (
        <Table<SourceStringResult>
          columnDefinitions={sourceColumnDefinitions}
          items={sourceItems}
          loading={loading}
          loadingText="Searching..."
          empty={emptyContent}
          variant="container"
          contentDensity="compact"
          resizableColumns={true}
          header={resultsHeader}
          pagination={paginationComponent}
        />
      ) : (
        <Table<TranslationResult>
          columnDefinitions={translationColumnDefinitions}
          items={translationItems}
          loading={loading}
          loadingText="Searching..."
          empty={emptyContent}
          variant="container"
          contentDensity="compact"
          resizableColumns={true}
          header={resultsHeader}
          pagination={paginationComponent}
        />
      )}
    </div>
  );
};

export default SearchResultsTable;
